import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import utils from '../../../../utils/utils';
import { user } from '../../../../redux/user';
import { PERMISSION } from '../../../../constants';
import config from '../../../../config';
import viewIcon from '../../../../assets/images/docs.svg';
import downloadIcon from '../../../../assets/images/download.svg';

// The stream endpoint is gated on the `management-company-id` header (authorizeOperationForManagementCompany),
// which a plain navigation/window.open cannot send — so both View and Download fetch the file (with the header
// + session cookie) and use an object URL. View opens it in a new tab; Download forces a save.
const parseFilename = (contentDisposition) => {
	const match = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/);
	return match ? match[1] : 'attachment';
};

const fetchAttachment = async (attachmentUrl, managementCompanyId) => {
	const response = await fetch(attachmentUrl, {
		credentials: 'include',
		headers: { 'management-company-id': managementCompanyId }
	});
	if (!response.ok) {
		throw new Error('Failed to fetch attachment');
	}
	const blob = await response.blob();
	return {
		objectUrl: window.URL.createObjectURL(blob),
		filename: parseFilename(response.headers.get('content-disposition'))
	};
};

const OnDemandStaffCredentialActions = ({ managementCompanyId, caregiverId, fileId }) => {
	const userData = useSelector(user, shallowEqual);
	const [hasError, setHasError] = useState(false);

	const hasPermission = utils.isAuthorizedExact(
		PERMISSION.WORKFORCE_SHIFTMED_VIEW,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const attachmentUrl = `${config.API_URL}/on-demand-staff/management-companies/${managementCompanyId}/professionals/${caregiverId}/attachment/${fileId}`;

	const onView = async () => {
		if (!hasPermission) {
			return;
		}
		setHasError(false);
		// Open the tab synchronously (before the await) so the pop-up is tied to the click gesture.
		const newTab = window.open('', '_blank');
		try {
			const { objectUrl } = await fetchAttachment(attachmentUrl, managementCompanyId);
			if (newTab) {
				newTab.location = objectUrl;
			}
		} catch {
			if (newTab) {
				newTab.close();
			}
			setHasError(true);
		}
	};

	const onDownload = async () => {
		if (!hasPermission) {
			return;
		}
		setHasError(false);
		try {
			const { objectUrl, filename } = await fetchAttachment(attachmentUrl, managementCompanyId);
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(objectUrl);
		} catch {
			setHasError(true);
		}
	};

	if (!fileId) {
		return null;
	}

	const buttonClassName = classNames('btn actions', { 'download-disabled': !hasPermission });
	const viewTooltip = hasPermission ? 'View attachment' : 'You do not have permission to view this attachment';
	const downloadTooltip = hasPermission
		? 'Download attachment'
		: 'You do not have permission to download this attachment';

	return (
		<div className="credential-actions">
			<div className="credential-actions-container">
				<OverlayTrigger
					placement="bottom-end"
					trigger={['hover']}
					overlay={<Tooltip className="download-permission-tooltip">{viewTooltip}</Tooltip>}
				>
					<Button type="button" className={buttonClassName} onClick={onView}>
						<img src={viewIcon} alt="View attachment" className="credential-action-icon" />
					</Button>
				</OverlayTrigger>
				<OverlayTrigger
					placement="bottom-end"
					trigger={['hover']}
					overlay={<Tooltip className="download-permission-tooltip">{downloadTooltip}</Tooltip>}
				>
					<Button type="button" className={buttonClassName} onClick={onDownload}>
						<img src={downloadIcon} alt="Download attachment" className="credential-action-icon" />
					</Button>
				</OverlayTrigger>
			</div>
			{hasError && (
				<span className="credential-action-error" role="alert">
					Something went wrong, please try again
				</span>
			)}
		</div>
	);
};

export default OnDemandStaffCredentialActions;
